import React, { useEffect, useState } from 'react';
import './MyAccountPage.scss';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useForm } from 'react-hook-form';
import Loader from '../../../shared/components/common/loader/Loader';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { useTranslation } from 'react-i18next';
import { SimpleUserDataResponse } from '../../../shared/models/auth/SimpleUserDataResponse';
import { saveMyAccountData } from './services/MyAccountService';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';

const MyAccountPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { accountResponse, refetchData } = useAccount();
  const [weeklyReport, setWeeklyReport] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<{ name: string; type: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const langOptions = [
    { name: t('polish', { lng: 'pl' }), type: 'pl' },
    { name: t('english', { lng: 'en' }), type: 'en' },
  ];

  useEffect(() => {
    if (!accountResponse) {
      return;
    }

    const userLang = accountResponse.language ?? 'en';
    const currentLang = langOptions.find((lang) => lang.type === userLang);
    setSelectedLanguage(currentLang || langOptions[1]);
    setWeeklyReport(accountResponse.weeklyReport ?? false);
  }, [accountResponse]);

  const onSubmit = async (formData: any) => {
    if (!accountResponse || !selectedLanguage) {
      return;
    }

    const data = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: accountResponse.email,
      language: selectedLanguage.type,
      weeklyReport,
    } as SimpleUserDataResponse;

    try {
      await saveMyAccountData(data);
      await i18n.changeLanguage(selectedLanguage.type);
      refetchData();
      accountDataChangedDialog();
    } catch (error) {
      console.error('Error saving account data:', error);
    }
  };

  const accountDataChangedDialog = () => {
    confirmDialog({
      message: t('yourDataChanged'),
      header: t('dataChanged'),
      defaultFocus: 'accept',
      acceptLabel: t('ok'),
    });
  };

  if (!accountResponse || !selectedLanguage) {
    return (
      <div className="my-account-page">
        <Loader />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="my-account-page">
      <ConfirmDialog className="dialog-box dialog-box--accept" />

      <div className="my-account-page__row">
        <div className="form form--inline">
          <div className="form__row form__row--inline">
            <div className="form__label">{t('firstName')}:</div>
            <InputText
              {...register('firstName', { required: t('fieldIsRequired') })}
              type="text"
              defaultValue={accountResponse.firstName}
              className={errors.firstName ? 'form__invalid' : ''}
            />
            {errors.firstName && <span>{errors.firstName.message as string}</span>}
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('lastName')}:</div>
            <InputText
              {...register('lastName', { required: t('fieldIsRequired') })}
              type="text"
              defaultValue={accountResponse.lastName}
              className={errors.lastName ? 'form__invalid' : ''}
            />
            {errors.lastName && <span>{errors.lastName.message as string}</span>}
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('emailAddress')}:</div>
            <InputText type="text" value={accountResponse.email} disabled={true} />
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('language')}:</div>
            <Dropdown
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.value)}
              options={langOptions}
              className="dropdown"
              optionLabel="name"
            />
          </div>
          <div className="form__row form__row--inline">
            <div className="form__label">{t('weeklyReports')}:</div>
            <Checkbox onChange={() => setWeeklyReport(!weeklyReport)} checked={weeklyReport} />
          </div>

          <Button className="btn btn--sm" label={t('save')} />
        </div>
        <div className="my-account-page__help">
          <div className="help-box">
            <label className="help-box__title">{t('changeYourName')}</label>
            <div>{t('changeNameMsg')}</div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default MyAccountPage;
